import { commandApiSlice } from '../apiSlice';

/**
 * Two-step voter verification against api.command.
 *
 * <p>An operator enters the last four digits of a voter's phone; the register sends that voter a
 * six-digit code; the operator asks the voter to read it back. The OTP goes to the VOTER, never to
 * the person driving this screen — the flow only makes sense with a human on the other end of a
 * phone call, and every string in the dialog is written for that.</p>
 *
 * <p>Lives on **api.command**, not api.query, even though it reads: it has a side effect (an SMS
 * leaves the building) and it mutates server state (the OTP is stored). Both endpoints are
 * `[AllowAnonymous]` server-side; the bearer `commandApiSlice` attaches is harmless and kept for
 * consistency with every other call in the app.</p>
 *
 * <p>The newer routes — `lookup`, `identify`, `recent`, `add-to-list` — are NOT anonymous: they
 * show a voter's name, so they need a signed-in user holding `voter_interaction_messaging`. They
 * are mutations (bar `recent`), so a lookup's names and masked numbers are never kept in a query
 * cache; the dialog also resets each one once it has read it.</p>
 */

export interface IInitiateVerificationRequest {
	/**
	 * Exactly four digits — the legacy, anonymous path. The server rejects anything else with a 400
	 * before it looks anything up. Omitted when texting a voter picked from a lookup.
	 */
	lastFourDigits?: string;
	/** 4–7 digits. Sent with `voterId`, so the server can re-check the pick before it texts. */
	phoneSuffix?: string;
	/**
	 * The voter picked from a lookup. Only ever sent once the capability probe has said the route
	 * exists — an older server would ignore it and text whoever matches the digits instead.
	 */
	voterId?: string;
	/**
	 * Required. The full INEC code, e.g. `13-05-07-005`. Narrows the digit match to one polling
	 * unit, so a 404 means nobody THERE has that number — not that nobody on the register does.
	 */
	pollingUnitCode: string;
}

export interface IInitiateVerificationResponse {
	voterId: string;
	/** Already masked by the server — e.g. `0806***7039`. Never mask this client-side. */
	maskedPhoneNumber: string;
	message: string;
	/** Seconds the code stays valid. 300 today; read it rather than hardcoding it. */
	expiresInSeconds: number;
	/**
	 * ISO instant the code stops working. Newer builds only — absent on today's api.command, so the
	 * client falls back to `expiresInSeconds` and, failing both, hides the countdown.
	 */
	expiresAt?: string;
}

export interface IConfirmVerificationRequest {
	voterId: string;
	otp: string;
}

/** The voter as the register holds them. Every field is a string except the id, which is a GUID. */
export interface IVerifiedVoter {
	id: string;
	firstName: string;
	lastName: string;
	phoneNumber: string;
	email: string;
	pollingUnitCode: string;
	stateCode: string;
	lgaCode: string;
	wardCode: string;
}

export interface IConfirmVerificationResponse {
	isVerified: boolean;
	voter: IVerifiedVoter;
}

/** api.command's `BaseResponse` envelope. Unlike api.query's Voters endpoints, this one is wrapped. */
interface IBaseResponse<T> {
	message: string;
	isSuccessful: boolean;
	statusCode: number;
	data: T;
}

export interface ILookupRequest {
	pollingUnitCode: string;
	/** 4–7 digits, the end of the voter's phone number. */
	phoneSuffix: string;
}

export interface ILookupPlace {
	code: string;
	name: string;
}

export interface ILookupPollingUnit extends ILookupPlace {
	ward: ILookupPlace | null;
	lga: ILookupPlace | null;
}

/** One voter a lookup found. No VIN, no email, no raw phone — the server never sends them. */
export interface IVoterMatch {
	voterId: string;
	firstName: string;
	lastName: string;
	/** Server-masked, e.g. `0806****039`. */
	maskedPhone: string;
}

export interface ILookupResponse {
	/** How many voters matched in total — may exceed `matches.length`. */
	matchCount: number;
	/** True when `matches` holds fewer than `matchCount`. */
	truncated: boolean;
	/** Null when the server could not name the polling unit. The lookup itself still stands. */
	pollingUnit: ILookupPollingUnit | null;
	/** At most five, in a stable order. */
	matches: IVoterMatch[];
}

/** Most matches a lookup returns. More than this and the operator asks for more digits. */
export const LOOKUP_MATCH_LIMIT = 5;

export interface IIdentifyRequest {
	voterId: string;
	pollingUnitCode: string;
	phoneSuffix: string;
}

export interface IIdentifyResponse {
	eventId: string;
	outcome: 'Identified';
	at: string;
}

export type VerificationOutcome = 'Identified' | 'Verified';

/** One of the caller's own recent checks. */
export interface IRecentVerification {
	eventId: string;
	voterId: string;
	voterName: string;
	pollingUnitCode: string;
	pollingUnitName: string | null;
	outcome: VerificationOutcome;
	at: string;
}

export interface IAddToListRequest {
	voterId: string;
	/** A contact-group id as api.core issues it (a string). */
	groupId: string;
	pollingUnitCode: string;
}

export interface IAddToListResponse {
	status: 'added' | 'alreadyInList';
	maskedPhone: string;
	groupId: string;
}

/** Whether this build of api.command has the lookup route, and whether this user may use it. */
export type LookupCapability = 'supported' | 'unsupported' | 'forbidden' | 'unknown';

/** The HTTP status of an RTK Query error, including one whose body was not JSON. */
export const errorStatusOf = (error: unknown): number | string | undefined => {
	const e = error as { status?: number | string; originalStatus?: number } | null | undefined;
	if (!e) return undefined;
	if (typeof e.status === 'number') return e.status;
	// An HTML 404/405 from a proxy fails to parse; RTK then reports PARSING_ERROR + originalStatus.
	return typeof e.originalStatus === 'number' ? e.originalStatus : e.status;
};

/**
 * Read the capability probe — an EMPTY-bodied POST to the lookup route.
 *
 * <p>A server with the route validates the body and answers 400 (or 429 when rate-limited), so
 * either proves it is there. 404/405 means this api.command predates it. 401/403 means it is there
 * but this user may not see names, so the dialog keeps the legacy flow. Anything else — a network
 * failure, a 5xx — is unknown, and unknown also keeps the legacy flow: the new UI is only ever
 * shown on positive evidence, and nothing here ever falls back to texting anyone.</p>
 */
export const classifyLookupCapability = (outcome: { error?: unknown } | { data: unknown }): LookupCapability => {
	if (!('error' in outcome) || !outcome.error) return 'supported';
	const status = errorStatusOf(outcome.error);
	if (status === 404 || status === 405) return 'unsupported';
	if (status === 400 || status === 422 || status === 429) return 'supported';
	if (status === 401 || status === 403) return 'forbidden';
	return 'unknown';
};

const text = (value: unknown): string => (typeof value === 'string' && value.trim() && value !== 'null' ? value.trim() : '');

const place = (raw: unknown): ILookupPlace | null => {
	const p = raw as Partial<ILookupPlace> | null | undefined;
	const code = text(p?.code);
	return code ? { code, name: text(p?.name) || code } : null;
};

/**
 * Make a lookup response safe to render, whatever the server sent.
 *
 * <p>Drops matches with no `voterId` (they cannot be identified or texted), caps the list at
 * five, and never lets `matchCount` fall below what is actually listed — a "2 voters share those
 * digits" above three names is a confident wrong number.</p>
 */
export const normalizeLookup = (raw: unknown): ILookupResponse => {
	const r = (raw ?? {}) as Partial<ILookupResponse>;
	const all = Array.isArray(r.matches) ? r.matches : [];
	const matches = all
		.filter((m): m is IVoterMatch => !!m && !!text(m.voterId))
		.slice(0, LOOKUP_MATCH_LIMIT)
		.map((m) => ({
			voterId: text(m.voterId),
			firstName: text(m.firstName),
			lastName: text(m.lastName),
			maskedPhone: text(m.maskedPhone),
		}));
	const reported = typeof r.matchCount === 'number' && Number.isFinite(r.matchCount) ? Math.max(0, Math.floor(r.matchCount)) : 0;
	const matchCount = Math.max(reported, matches.length);
	const unit = place(r.pollingUnit);
	const pu = r.pollingUnit as Partial<ILookupPollingUnit> | null | undefined;
	return {
		matchCount,
		truncated: r.truncated === true || matchCount > matches.length,
		pollingUnit: unit ? { ...unit, ward: place(pu?.ward), lga: place(pu?.lga) } : null,
		matches,
	};
};

const voterVerificationApi = commandApiSlice.injectEndpoints({
	endpoints: (builder) => ({
		initiateVoterVerification: builder.mutation<IInitiateVerificationResponse, IInitiateVerificationRequest>({
			query: (body) => ({ method: 'POST', url: '/i-elect/Voters/verification/initiate', body }),
			transformResponse: (res: IBaseResponse<IInitiateVerificationResponse>) => res.data,
		}),
		confirmVoterVerification: builder.mutation<IConfirmVerificationResponse, IConfirmVerificationRequest>({
			query: (body) => ({ method: 'POST', url: '/i-elect/Voters/verification/confirm', body }),
			transformResponse: (res: IBaseResponse<IConfirmVerificationResponse>) => res.data,
			invalidatesTags: ['VoterVerificationRecent'],
		}),
		/** The capability probe. An empty body on purpose — see `classifyLookupCapability`. */
		probeVoterLookup: builder.mutation<unknown, undefined>({
			query: () => ({ method: 'POST', url: '/i-elect/Voters/verification/lookup', body: {} }),
		}),
		lookupVoter: builder.mutation<ILookupResponse, ILookupRequest>({
			query: (body) => ({ method: 'POST', url: '/i-elect/Voters/verification/lookup', body }),
			transformResponse: (res: IBaseResponse<unknown>) => normalizeLookup(res?.data),
		}),
		identifyVoter: builder.mutation<IIdentifyResponse, IIdentifyRequest>({
			query: (body) => ({ method: 'POST', url: '/i-elect/Voters/verification/identify', body }),
			transformResponse: (res: IBaseResponse<IIdentifyResponse>) => res.data,
			invalidatesTags: ['VoterVerificationRecent'],
		}),
		recentVerifications: builder.query<IRecentVerification[], { take?: number }>({
			query: (args) => ({ url: '/i-elect/Voters/verification/recent', params: { take: args.take ?? 20 } }),
			transformResponse: (res: IBaseResponse<IRecentVerification[] | null>) => (Array.isArray(res?.data) ? res.data : []),
			providesTags: ['VoterVerificationRecent'],
			// Names, briefly: long enough to survive a step change, short enough not to linger.
			keepUnusedDataFor: 30,
		}),
		addVoterToList: builder.mutation<IAddToListResponse, IAddToListRequest>({
			query: (body) => ({ method: 'POST', url: '/i-elect/Voters/verification/add-to-list', body }),
			transformResponse: (res: IBaseResponse<IAddToListResponse>) => res.data,
		}),
	}),
});

export const {
	useInitiateVoterVerificationMutation,
	useConfirmVoterVerificationMutation,
	useProbeVoterLookupMutation,
	useLookupVoterMutation,
	useIdentifyVoterMutation,
	useRecentVerificationsQuery,
	useAddVoterToListMutation,
} = voterVerificationApi;
